<script setup>
import { computed } from "vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import AlertMessage from "@/components/AlertMessage.vue";

const props = defineProps({
  stopDate: { type: String, default: "" },
  stopNumber: { type: String, default: "" },
  isModification: { type: Boolean, default: false },
  decaVersion: { type: Number, default: 1 },
  shipperName: { type: String, default: "" },
  shipperNameError: { type: Boolean, default: false },
  shipperVatNumber: { type: String, default: "" },
  shipperVatNumberError: { type: Boolean, default: false },
  shipperAddress: { type: String, default: "" },
  shipperAddressError: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update:shipperName",
  "update:shipperVatNumber",
  "update:shipperAddress",
  "next",
]);

const shipperNameModel = computed({
  get: () => props.shipperName,
  set: (value) => emit("update:shipperName", value),
});
const shipperVatNumberModel = computed({
  get: () => props.shipperVatNumber,
  set: (value) => emit("update:shipperVatNumber", value),
});
const shipperAddressModel = computed({
  get: () => props.shipperAddress,
  set: (value) => emit("update:shipperAddress", value),
});
</script>

<template>
  <div
    class="multisteps-form__panel js-active p-2 position-relative bg-white rounded-3"
    data-animation="FadeIn"
  >
    <div class="d-flex flex-column" style="height: 420px; overflow-y: auto; overflow-x: hidden;">
      <alert-message
        v-if="isModification"
        title="Aviso"
        type="info"
        class="mb-3"
      >
        Vas a modificar el DeCA (versión <strong>{{ decaVersion }}</strong>) de
        la parada <strong>{{ stopNumber || "-" }}</strong> del
        <strong>{{ stopDate || "-" }}</strong>. Se han precargado los datos
        del DeCA vigente — revísalos y corrígelos antes de guardar. Se
        generará una nueva versión del documento; la anterior queda
        archivada como histórico.
      </alert-message>
      <alert-message v-else title="Aviso" type="info" class="mb-3">
        Vas a generar el DeCA de la parada <strong>{{ stopNumber || "-" }}</strong>
        del <strong>{{ stopDate || "-" }}</strong>. Todos los datos se han
        precargado con la mejor información disponible — revísalos y
        corrígelos si hace falta antes de generar el documento. Al generar
        el DeCA, la parada quedará bloqueada para nuevos bultos.
      </alert-message>

      <div class="text-center mb-3">
        <p class="mb-0">Revisa los datos del cargador</p>
      </div>

      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label for="deca-shipper-name" class="col-form-label required">
            Razón social
          </label>
        </div>
        <div class="col">
          <argon-input
            id="deca-shipper-name"
            v-model="shipperNameModel"
            type="text"
            :max-length="40"
            :error="shipperNameError"
          />
        </div>
      </div>
      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label for="deca-shipper-vat" class="col-form-label required">NIF</label>
        </div>
        <div class="col">
          <argon-input
            id="deca-shipper-vat"
            v-model="shipperVatNumberModel"
            type="text"
            :max-length="25"
            :error="shipperVatNumberError"
          />
        </div>
      </div>
      <div class="row modal-form-row mb-0">
        <div class="modal-form-label">
          <label for="deca-shipper-address" class="col-form-label required">
            Domicilio
          </label>
        </div>
        <div class="col">
          <argon-input
            id="deca-shipper-address"
            v-model="shipperAddressModel"
            type="text"
            :max-length="120"
            :error="shipperAddressError"
          />
        </div>
      </div>

      <div class="mt-auto text-end">
        <argon-button
          class="mb-0 ms-auto btn-icon-only"
          color="primary"
          variant="outline"
          size="xs"
          aria-label="Siguiente"
          @click.prevent="emit('next')"
        >
          <span class="btn-inner--icon">
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </span>
        </argon-button>
      </div>
    </div>
  </div>
</template>
